import React, {useEffect, useState} from "react";
import {useGlobal} from "../../Store";
import {Link} from "react-router-dom";
import {postData} from "../../Utils/Server";
import Endpoints from "../../Constants/Endpoints";
import {useHistory} from "react-router-dom";
import {updateDocumentTitle} from "../../Utils/Common";
import styled from "styled-components";
import PageLayout from "../Layout/PageLayout";
import {Avatar, Button, Card, Spinner, StatusBadge, Toolbar, Select, Toggle} from "../UI";
import {tokens, mq} from "../../theme/tokens";

const CardGrid = styled.div`
    display: grid;
    gap: ${tokens.spacing[5]};
`;

const RequestHead = styled.div`
    display: flex;
    align-items: center;
    gap: ${tokens.spacing[3]};
    margin-bottom: ${tokens.spacing[4]};
    padding-bottom: ${tokens.spacing[4]};
    border-bottom: 1px solid ${tokens.colors.divider};
`;

const Photo = styled.img`
    width: 48px;
    height: 48px;
    border-radius: ${tokens.radii.circle};
    object-fit: cover;
    flex-shrink: 0;
    background: ${tokens.colors.surfaceSunken};
`;

const HeadText = styled.div`
    flex: 1;
    min-width: 0;
    a { text-decoration: none; }
`;

const RequestTitle = styled.span`
    font-size: ${tokens.typography.size.md};
    font-weight: ${tokens.typography.weight.bold};
    color: ${tokens.colors.heading};
`;

const Code = styled.span`
    font-size: ${tokens.typography.size.sm};
    font-weight: ${tokens.typography.weight.semibold};
    color: ${tokens.colors.muted};
    flex-shrink: 0;
`;

const Split = styled.div`
    display: grid;
    gap: ${tokens.spacing[5]};
    grid-template-columns: 1fr;
    ${mq.md} { grid-template-columns: 1fr 2fr; }
`;

const KV = styled.table`
    width: 100%;
    border-collapse: collapse;
    font-size: ${tokens.typography.size.base};

    td {
        padding: ${tokens.spacing[2]} ${tokens.spacing[3]};
        border-bottom: 1px solid ${tokens.colors.divider};
        vertical-align: middle;
    }
    td:first-child { color: ${tokens.colors.muted}; white-space: nowrap; }
    td:last-child {
        color: ${tokens.colors.heading};
        font-weight: ${tokens.typography.weight.semibold};
        text-align: right;
    }
    tr:last-child td { border-bottom: 0; }
`;

const Grid = styled.table`
    width: 100%;
    border-collapse: collapse;
    font-size: ${tokens.typography.size.base};

    th {
        text-align: left;
        padding: ${tokens.spacing[2]} ${tokens.spacing[3]};
        font-size: ${tokens.typography.size.xs};
        font-weight: ${tokens.typography.weight.bold};
        letter-spacing: 0.06em;
        text-transform: uppercase;
        color: ${tokens.colors.muted};
        background: ${tokens.colors.cream};
        border-bottom: 1px solid ${tokens.colors.border};
    }
    td {
        padding: ${tokens.spacing[2]} ${tokens.spacing[3]};
        border-bottom: 1px solid ${tokens.colors.divider};
        color: ${tokens.colors.body};
    }
    tr:last-child td { border-bottom: 0; }
`;

const Footer = styled.div`
    margin-top: ${tokens.spacing[5]};
    a { text-decoration: none; }
`;

const RequestsList = props => {
    let history = useHistory();

    const {id} = props.match.params;
    const [globalState, globalActions] = useGlobal();
    const [isLoading, setIsLoading] = useState(false);
    const [activeList, setActiveList] = useState([]);

    // Developer-API provenance filters. Test-mode deliveries are hidden by
    // default (test isolation) until "Include test" is toggled on.
    const [source, setSource] = useState('all');
    const [includeTest, setIncludeTest] = useState(false);

    const load = (loadMore) => {
        let data = new FormData();
        data.append("type", id);
        data.append("offset", loadMore ? activeList.length : 0);
        if (source !== 'all') data.append("source", source);
        if (includeTest) data.append("include_test", "1");

        setIsLoading(true);

        postData(Endpoints.REQUEST_LIST, data)
            .then(res => res.json())
            .then(res => {
                setIsLoading(false);
                let currentPosts = loadMore ? activeList : [];
                currentPosts = currentPosts.concat(...res.data);
                setActiveList(currentPosts);
            })
            .catch((error) => {
                setIsLoading(false);
                console.log(error)
            });
    };

    useEffect(() => {
        updateDocumentTitle(id.toUpperCase())
        load(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [source, includeTest]);

    return (
        <PageLayout
            title={id.toUpperCase()}
            actions={isLoading && <Spinner size={20}/>}
        >
            <Toolbar style={{marginBottom: tokens.spacing[4]}}>
                <Select value={source} onChange={e => setSource(e.target.value)}>
                    <option value="all">All sources</option>
                    <option value="app">App</option>
                    <option value="api">API</option>
                </Select>
                <Toolbar.Spacer/>
                <Toggle checked={includeTest} onChange={e => setIncludeTest(e.target.checked)} label="Include test"/>
            </Toolbar>

            <CardGrid>
                {activeList.map((i, k) =>
                    <Card key={k}>
                        <RequestHead>
                            <Link to={"/request/" + i.id}>
                                <Avatar
                                    src={i.user_details.photo ? Endpoints.SERVER_HOST + Endpoints.FILE_URL + i.user_details.photo : undefined}
                                    name={i.user_details.name}
                                    size={48}/>
                            </Link>
                            <HeadText>
                                <Link to={"/request/" + i.id}>
                                    <RequestTitle>Pick up request at {i.pick_up_location}</RequestTitle>
                                </Link>
                            </HeadText>
                            <Code>#{i.request_code}</Code>
                            {i.source === 'api' &&
                            <StatusBadge tone="info" size="sm" title={i.external_reference ? `ref: ${i.external_reference}` : undefined}>API</StatusBadge>}
                            {i.mode === 'test' &&
                            <StatusBadge tone="warn" size="sm">TEST</StatusBadge>}
                        </RequestHead>

                        <Split>
                            <div>
                                <KV>
                                    <tbody>
                                    <tr>
                                        <td>Delivery Type</td>
                                        <td>{i.delivery_type}</td>
                                    </tr>
                                    <tr>
                                        <td>Means</td>
                                        <td>{i.means}</td>
                                    </tr>
                                    <tr>
                                        <td>Pick Up Type</td>
                                        <td>{i.pick_up_type}</td>
                                    </tr>
                                    {i.is_scheduled === 1 &&
                                    <tr>
                                        <td>Pick Up Time</td>
                                        <td>{i.pick_up_date_time_string}</td>
                                    </tr>
                                    }
                                    <tr>
                                        <td>Fee</td>
                                        <td>KES {i.fee}</td>
                                    </tr>
                                    <tr>
                                        <td>Expected Completion Time</td>
                                        <td>{i.expected_end_time_string}</td>
                                    </tr>
                                    <tr>
                                        <td>Request Status</td>
                                        <td><StatusBadge status={i.status}/></td>
                                    </tr>

                                    {i.source === 'api' &&
                                    <tr>
                                        <td>Source</td>
                                        <td>API{i.api_key_id ? ` · ${i.api_key_id}` : ''}</td>
                                    </tr>}
                                    {i.source === 'api' && i.external_reference &&
                                    <tr>
                                        <td>External reference</td>
                                        <td>{i.external_reference}</td>
                                    </tr>}

                                    {i.status === "CANCELLED" &&
                                    <tr>
                                        <td>Cancellation Reason</td>
                                        <td>{i.cancellation_reason}</td>
                                    </tr>
                                    }

                                    <tr>
                                        <td>Progress Status</td>
                                        <td>{i.progress_status}</td>
                                    </tr>
                                    <tr>
                                        <td>Perf. Status</td>
                                        <td>{i.performance_status}</td>
                                    </tr>
                                    </tbody>
                                </KV>
                            </div>
                            <div>
                                <Grid>
                                    <thead>
                                    <tr>
                                        <th>Drop Off</th>
                                        <th>Recipient</th>
                                    </tr>
                                    </thead>
                                    <tbody>
                                    {i.drop_offs.map((i, k) => <tr key={k}>
                                        <td>{i.address}</td>
                                        <td>{i.phone}</td>
                                    </tr>)}
                                    </tbody>
                                </Grid>
                            </div>
                        </Split>

                        <Footer>
                            <Link to={"/request/" + i.id}>
                                <Button>
                                    {i.status === "PENDING" && i.rider_id === 0 ? "Assign Rider One" : "View Details"}
                                </Button>
                            </Link>
                        </Footer>
                    </Card>
                )}
            </CardGrid>
        </PageLayout>
    )
};

export default RequestsList;
